import { ChangeEvent, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faClose, faDownload, faFileAlt, faFilter, faPaperPlane, faTrashAlt, faUpload } from '@fortawesome/free-solid-svg-icons';
import PromotoraService from '../../../services/promotoraService';
import { PromotoraDTO } from '../../../shared/dto/promotoraDTO';
import { PromotoraDocumentoDTO } from '../../../shared/dto/promotoraDocumentoDTO';
import Spinner from '../../../shared/spinner';

interface PromotoraDocumentacaoModalProps {
  show: boolean;
  promotora: PromotoraDTO | null;
  somenteConsulta?: boolean;
  somentePendentesInicial?: boolean;
  onHide: () => void;
  onUpdated?: () => void;
}

type AlertState = { type: 'success' | 'warning' | 'danger'; text: string };
type ArquivoSelecionado = { nomeArquivo: string; arquivoBase64: string };
type FiltroDocumento = 'TODOS' | 'PENDENTES' | 'OBRIGATORIOS' | 'NAO_OBRIGATORIOS';

const statusPermiteEdicao = ['PENDENTE_DOCUMENTACAO', 'DOCUMENTACAO_PARCIAL', 'MAIS_DOCUMENTACAO'];
const statusChecklistConcluido = ['ANEXADO', 'NAO_SE_APLICA'];

const obterMensagemErro = async (err: any, fallback: string) => {
  const data = err.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();
    return text || fallback;
  }

  if (data instanceof ArrayBuffer) {
    const text = new TextDecoder().decode(data);
    return text || fallback;
  }

  return typeof data === 'string' ? data : fallback;
};

function PromotoraDocumentacaoModal({ show, promotora, somenteConsulta = false, somentePendentesInicial = false, onHide, onUpdated }: PromotoraDocumentacaoModalProps) {
  const promotoraService = new PromotoraService();
  const [loading, setLoading] = useState(false);
  const [documentos, setDocumentos] = useState<PromotoraDocumentoDTO[]>([]);
  const [arquivosSelecionados, setArquivosSelecionados] = useState<Record<string, ArquivoSelecionado>>({});
  const [inputResetKey, setInputResetKey] = useState(0);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [filtroDocumento, setFiltroDocumento] = useState<FiltroDocumento>('TODOS');
  const [naoObrigatoriosSelecionados, setNaoObrigatoriosSelecionados] = useState<string[]>([]);

  const podeEditar = !!promotora?.statusGestao && statusPermiteEdicao.includes(promotora.statusGestao) && !somenteConsulta;
  const apenasMaisDocumentacao = promotora?.statusGestao === 'MAIS_DOCUMENTACAO';
  const podeEditarDocumento = (documento: PromotoraDocumentoDTO) => podeEditar
    && (!apenasMaisDocumentacao || !!documento.pendenteCorrecao);
  const checklistCompleto = documentos.length > 0
    && documentos.every((documento) => statusChecklistConcluido.includes(documento.statusChecklist));
  const documentosSolicitadosCorrigidos = documentos.every((documento) => !documento.pendenteCorrecao);
  const podeFinalizar = apenasMaisDocumentacao ? documentosSolicitadosCorrigidos : checklistCompleto;
  const temArquivosParaDownload = documentos.some((documento) => documento.id && !documento.naoSeAplica && documento.statusChecklist === 'ANEXADO');
  const obterChaveDocumento = (documento: PromotoraDocumentoDTO) => documento.documentoAdicional
    ? `adicional-${documento.id || documento.descricaoDocumento}`
    : `checklist-${documento.codigoChecklist}`;

  const documentosOrdenados = [...documentos].sort((a, b) => {
    if (!!a.arquivoObrigatorio !== !!b.arquivoObrigatorio) return a.arquivoObrigatorio ? -1 : 1;
    if (!!a.documentoAdicional !== !!b.documentoAdicional) return a.documentoAdicional ? 1 : -1;
    return (a.codigoChecklist || 9999) - (b.codigoChecklist || 9999);
  });

  const documentosVisiveis = documentosOrdenados.filter((documento) => {
    if (filtroDocumento === 'PENDENTES') {
      if (apenasMaisDocumentacao) return !!documento.pendenteCorrecao;
      return !statusChecklistConcluido.includes(documento.statusChecklist) || documento.pendenteCorrecao;
    }
    if (filtroDocumento === 'OBRIGATORIOS') return !!documento.arquivoObrigatorio;
    if (filtroDocumento === 'NAO_OBRIGATORIOS') return !documento.arquivoObrigatorio && !documento.documentoAdicional;
    return true;
  });

  const listarDocumentos = async (exibirLoading = true) => {
    if (!promotora?.id) return;

    if (exibirLoading) {
      setLoading(true);
    }

    try {
      const response = await promotoraService.listarDocumentosPromotora(promotora.id);
      setDocumentos(response.data || []);
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao listar documentos.') });
    } finally {
      if (exibirLoading) {
        setLoading(false);
      }
    }
  };

  const baixarBlob = (dados: BlobPart, tipo: string, nomeArquivo: string) => {
    const blob = new Blob([dados], { type: tipo || 'application/octet-stream' });
    const urlBlob = window.URL.createObjectURL(blob);
    const linkDownload = document.createElement('a');
    linkDownload.href = urlBlob;
    linkDownload.download = nomeArquivo;
    document.body.appendChild(linkDownload);
    linkDownload.click();
    document.body.removeChild(linkDownload);
    window.URL.revokeObjectURL(urlBlob);
  };

  const handleArquivo = (chaveDocumento: string, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setAlert(null);

    if (!file) {
      setArquivosSelecionados((prev) => {
        const novo = { ...prev };
        delete novo[chaveDocumento];
        return novo;
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') {
        setArquivosSelecionados((prev) => ({
          ...prev,
          [chaveDocumento]: {
            nomeArquivo: file.name,
            arquivoBase64: e.target?.result as string
          }
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const limparArquivoSelecionado = (chaveDocumento: string) => {
    setArquivosSelecionados((prev) => {
      const novo = { ...prev };
      delete novo[chaveDocumento];
      return novo;
    });
    setInputResetKey((prev) => prev + 1);
  };

  const uploadArquivo = async (documento: PromotoraDocumentoDTO) => {
    if (!promotora?.id) return;

    const chaveDocumento = obterChaveDocumento(documento);
    const arquivo = arquivosSelecionados[chaveDocumento];
    if (!arquivo?.arquivoBase64) {
      setAlert({ type: 'warning', text: 'Selecione um arquivo para este item.' });
      return;
    }

    setLoading(true);
    try {
      const dto = new PromotoraDocumentoDTO();
      dto.idPromotora = Number(promotora.id);
      dto.id = documento.id;
      dto.codigoChecklist = documento.documentoAdicional ? undefined as any : documento.codigoChecklist;
      dto.documentoAdicional = !!documento.documentoAdicional;
      dto.descricaoDocumento = documento.descricaoDocumento || documento.nomeChecklist;
      dto.nomeArquivo = arquivo.nomeArquivo;
      dto.arquivoBase64 = arquivo.arquivoBase64.split(',')[1];
      dto.naoSeAplica = false;

      await promotoraService.incluirDocumentoPromotora(dto);
      limparArquivoSelecionado(chaveDocumento);
      setAlert({ type: 'success', text: 'Arquivo anexado com sucesso.' });
      await listarDocumentos(false);
      onUpdated?.();
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao anexar arquivo.') });
    } finally {
      setLoading(false);
    }
  };

  const marcarNaoSeAplica = async (documento: PromotoraDocumentoDTO) => {
    if (!promotora?.id) return;

    if (!documento.permiteNaoSeAplica) {
      setAlert({ type: 'warning', text: 'Este item exige arquivo.' });
      return;
    }

    setLoading(true);
    try {
      const dto = new PromotoraDocumentoDTO();
      dto.idPromotora = Number(promotora.id);
      dto.codigoChecklist = documento.codigoChecklist;
      dto.naoSeAplica = true;

      await promotoraService.incluirDocumentoPromotora(dto);
      limparArquivoSelecionado(obterChaveDocumento(documento));
      setAlert({ type: 'success', text: 'Item marcado como não se aplica.' });
      await listarDocumentos(false);
      onUpdated?.();
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao marcar item como não se aplica.') });
    } finally {
      setLoading(false);
    }
  };

  const alternarSelecaoNaoObrigatorios = (selecionar: boolean) => {
    if (!selecionar) {
      setNaoObrigatoriosSelecionados([]);
      return;
    }

    const selecionados = documentosOrdenados
      .filter(podeEditarDocumento)
      .filter((documento) => !documento.arquivoObrigatorio && !documento.documentoAdicional && documento.permiteNaoSeAplica)
      .filter((documento) => documento.statusChecklist !== 'NAO_SE_APLICA')
      .map(obterChaveDocumento);
    setNaoObrigatoriosSelecionados(selecionados);
  };

  const marcarSelecionadosComoNaoSeAplica = async () => {
    const selecionados = documentosOrdenados.filter((documento) => naoObrigatoriosSelecionados.includes(obterChaveDocumento(documento)));
    if (selecionados.length === 0) {
      setAlert({ type: 'warning', text: 'Selecione os documentos não obrigatórios.' });
      return;
    }

    setLoading(true);
    try {
      for (const documento of selecionados) {
        const dto = new PromotoraDocumentoDTO();
        dto.idPromotora = Number(promotora?.id);
        dto.codigoChecklist = documento.codigoChecklist;
        dto.naoSeAplica = true;
        await promotoraService.incluirDocumentoPromotora(dto);
      }
      setNaoObrigatoriosSelecionados([]);
      setAlert({ type: 'success', text: 'Itens selecionados marcados como não se aplica.' });
      await listarDocumentos(false);
      onUpdated?.();
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao marcar itens como não se aplica.') });
    } finally {
      setLoading(false);
    }
  };

  const excluirArquivo = async (documento: PromotoraDocumentoDTO) => {
    if (!documento.id) return;

    setLoading(true);
    try {
      await promotoraService.excluirDocumentoPromotora(documento.id);
      setAlert({ type: 'success', text: 'Documento removido do item.' });
      await listarDocumentos(false);
      onUpdated?.();
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao excluir documento.') });
    } finally {
      setLoading(false);
    }
  };

  const downloadArquivo = async (documento: PromotoraDocumentoDTO) => {
    if (!documento.id || documento.naoSeAplica) return;

    setLoading(true);
    try {
      const resp = await promotoraService.downloadDocumentoPromotora(documento.id);
      baixarBlob(resp.data, resp.headers['content-type'], documento.nomeArquivo || 'documento-promotora');
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao baixar arquivo.') });
    } finally {
      setLoading(false);
    }
  };

  const downloadModelo = async (documento: PromotoraDocumentoDTO) => {
    if (!documento.codigoChecklist) return;

    setLoading(true);
    try {
      const resp = await promotoraService.downloadModeloDocumentoPromotora(documento.codigoChecklist);
      baixarBlob(resp.data, resp.headers['content-type'], documento.modeloArquivo || `modelo-documento-${documento.codigoChecklist}.docx`);
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao baixar modelo.') });
    } finally {
      setLoading(false);
    }
  };

  const downloadTodos = async () => {
    if (!promotora?.id) return;

    if (!temArquivosParaDownload) {
      setAlert({ type: 'warning', text: 'Não há documentos anexados para baixar.' });
      return;
    }

    setLoading(true);
    try {
      const resp = await promotoraService.downloadLoteDocumentosPromotora(promotora.id);
      baixarBlob(resp.data, resp.headers['content-type'], `Promotora_${promotora.id}_documentos.zip`);
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao baixar documentos.') });
    } finally {
      setLoading(false);
    }
  };

  const finalizarDocumentacao = async () => {
    if (!promotora?.id) return;

    if (apenasMaisDocumentacao && !documentosSolicitadosCorrigidos) {
      setAlert({ type: 'warning', text: 'Conclua os documentos solicitados para correção antes de enviar.' });
      return;
    }

    if (!apenasMaisDocumentacao && !checklistCompleto) {
      setAlert({ type: 'warning', text: 'Finalize todos os itens da checklist antes de enviar para análise.' });
      return;
    }

    setLoading(true);
    try {
      await promotoraService.finalizarDocumentacaoPromotora(promotora.id);
      setAlert({ type: 'success', text: 'Documentação finalizada com sucesso.' });
      onUpdated?.();
      onHide();
    } catch (err: any) {
      setAlert({ type: 'danger', text: await obterMensagemErro(err, 'Erro ao finalizar documentação.') });
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'ANEXADO':
        return 'Anexado';
      case 'NAO_SE_APLICA':
        return 'Não se aplica';
      case 'PENDENTE_CORRECAO':
        return 'Pendente correção';
      default:
        return 'Pendente';
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'ANEXADO':
        return 'bg-success';
      case 'NAO_SE_APLICA':
        return 'bg-secondary';
      case 'PENDENTE_CORRECAO':
        return 'bg-primary';
      default:
        return 'bg-warning text-dark';
    }
  };

  const formatarBooleano = (valor: boolean) => (valor ? 'Sim' : 'Não');

  const formatarData = (data?: Date) => {
    if (!data) return '-';
    return format(new Date(data), 'dd/MM/yyyy HH:mm');
  };

  useEffect(() => {
    if (show) {
      setAlert(null);
      setArquivosSelecionados({});
      setNaoObrigatoriosSelecionados([]);
      setFiltroDocumento((somentePendentesInicial || promotora?.statusGestao === 'MAIS_DOCUMENTACAO') ? 'PENDENTES' : 'TODOS');
      setInputResetKey((prev) => prev + 1);
      listarDocumentos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, promotora?.id]);

  return (
    <Modal show={show} onHide={onHide} fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>Documentação da Promotora</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 'calc(100vh - 125px)', overflowY: 'auto' }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <strong>{promotora?.id} - {promotora?.nome}</strong>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
            <span className="badge bg-secondary">{promotora?.statusGestaoDescricao || promotora?.statusGestao || '-'}</span>
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faFilter} />
              <Form.Select size="sm" value={filtroDocumento} onChange={(e) => setFiltroDocumento(e.target.value as FiltroDocumento)} style={{ width: '210px' }}>
                <option value="TODOS">Todos os documentos</option>
                <option value="PENDENTES">Apenas pendentes</option>
                <option value="OBRIGATORIOS">Obrigatórios</option>
                <option value="NAO_OBRIGATORIOS">Não obrigatórios</option>
              </Form.Select>
            </div>
            {podeEditar && (
              <>
                <Form.Check
                  type="checkbox"
                  label="Selecionar não obrigatórios"
                  checked={naoObrigatoriosSelecionados.length > 0}
                  onChange={(e) => alternarSelecaoNaoObrigatorios(e.target.checked)}
                />
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={marcarSelecionadosComoNaoSeAplica}
                  disabled={loading || naoObrigatoriosSelecionados.length === 0}
                >
                  <FontAwesomeIcon icon={faBan} className="me-2" />
                  Não se aplica
                </Button>
              </>
            )}
            <Button size="sm" variant="outline-secondary" onClick={downloadTodos} disabled={loading || !temArquivosParaDownload}>
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Baixar anexos
            </Button>
            {podeEditar && (
              <Button size="sm" id="App-button-cor-principal-escura" onClick={finalizarDocumentacao} disabled={loading || !podeFinalizar}>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Finalizar
              </Button>
            )}
          </div>
        </div>

        {alert && <Alert variant={alert.type}>{alert.text}</Alert>}
        {loading && <Spinner loading={loading} />}

        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle">
            <thead>
              <tr>
                <th id="App-header-table" style={{ minWidth: '260px' }}>Documento</th>
                <th id="App-header-table" style={{ minWidth: '120px' }}>Vigência</th>
                <th id="App-header-table" style={{ minWidth: '110px' }}>Apresentação</th>
                <th id="App-header-table" style={{ minWidth: '120px' }}>Imp. Abertura</th>
                <th id="App-header-table" style={{ minWidth: '130px' }}>Imp. Bloqueio</th>
                <th id="App-header-table" style={{ minWidth: '120px' }}>Status</th>
                <th id="App-header-table" style={{ minWidth: '190px' }}>Arquivo</th>
                <th id="App-header-table" style={{ minWidth: '150px' }}>Usuário</th>
                <th id="App-header-table" style={{ minWidth: '140px' }}>Data e Hora</th>
                <th id="App-header-table" style={{ minWidth: podeEditar ? '360px' : '150px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {documentosVisiveis.map((documento) => {
                const chaveDocumento = obterChaveDocumento(documento);
                return (
                <tr key={chaveDocumento}>
                  <td>
                    <div className="fw-semibold">
                      {documento.codigoChecklist ? `${documento.codigoChecklist}. ` : ''}{documento.nomeChecklist}
                    </div>
                    {documento.arquivoObrigatorio && (
                      <span className="badge bg-danger mt-1">Arquivo obrigatório</span>
                    )}
                    {!documento.arquivoObrigatorio && !documento.documentoAdicional && (
                      <span className="badge bg-light text-dark border mt-1">Não obrigatório</span>
                    )}
                    {documento.documentoAdicional && (
                      <span className="badge bg-info text-dark mt-1">Novo documento solicitado</span>
                    )}
                    {documento.observacaoPendencia && (
                      <div className="small text-muted mt-1">{documento.observacaoPendencia}</div>
                    )}
                  </td>
                  <td>{documento.vigencia || '-'}</td>
                  <td>{documento.apresentacao || '-'}</td>
                  <td>{formatarBooleano(!!documento.impeditivoAbertura)}</td>
                  <td>{formatarBooleano(!!documento.impeditivoBloqueioCodigo)}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass(documento.statusChecklist)}`}>
                      {statusLabel(documento.statusChecklist)}
                    </span>
                  </td>
                  <td>{documento.nomeArquivo || '-'}</td>
                  <td>{documento.nomeUsuarioCadastro || '-'}</td>
                  <td>{formatarData(documento.dtInclusao)}</td>
                  <td>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {documento.possuiModelo && (
                        <Button size="sm" variant="outline-secondary" title="Baixar modelo" onClick={() => downloadModelo(documento)} disabled={loading}>
                          <FontAwesomeIcon icon={faFileAlt} />
                        </Button>
                      )}
                      {documento.id && !documento.naoSeAplica && documento.statusChecklist === 'ANEXADO' && (
                        <Button size="sm" title="Baixar anexo" onClick={() => downloadArquivo(documento)} disabled={loading}>
                          <FontAwesomeIcon icon={faDownload} />
                        </Button>
                      )}
                      {podeEditarDocumento(documento) && documento.id && (
                        <Button size="sm" variant="danger" title="Excluir" onClick={() => excluirArquivo(documento)} disabled={loading}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      )}
                      {podeEditarDocumento(documento) && (
                        <>
                          <input
                            key={`${chaveDocumento}-${inputResetKey}`}
                            type="file"
                            className="form-control form-control-sm"
                            style={{ width: '180px' }}
                            onChange={(event) => handleArquivo(chaveDocumento, event)}
                            disabled={loading}
                          />
                          <Button
                            size="sm"
                            variant="success"
                            title="Anexar"
                            onClick={() => uploadArquivo(documento)}
                            disabled={loading || !arquivosSelecionados[chaveDocumento]?.arquivoBase64}
                          >
                            <FontAwesomeIcon icon={faUpload} />
                          </Button>
                          {documento.permiteNaoSeAplica && (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              title="Não se aplica"
                              onClick={() => marcarNaoSeAplica(documento)}
                              disabled={loading || documento.naoSeAplica}
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
              {documentosVisiveis.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted">Nenhum documento encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FontAwesomeIcon icon={faClose} className="me-2" />
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PromotoraDocumentacaoModal;
